import React, { useEffect, useState } from "react";
import { Table, Button, Form, Spinner, Badge } from "react-bootstrap";
import ModalConfirmar from "../../components/ModalConfirmar";
import ModalEditarGastoFijo from "./ModalEditarGastoFijo";
import { useAuth } from "../../auth/AuthContext";
import { TOTAL_CUENTA_ID } from "../../components/SelectorCuenta";
import * as gastosFijosApi from "../../api/gastosFijos";
import * as reportesApi from "../../api/reportes";
import { fechaCobroDe, diasHastaVencer } from "../../utils/gastosFijos";
import { formatoNumero } from "../../utils/moneda";

const HOY = new Date();

export default function GastosFijos({ cuentaId, cuentas = [], onSaldoCambiado }) {
  const { esAdministrador } = useAuth();
  const [mes] = useState(HOY.getMonth() + 1);
  const [anio] = useState(HOY.getFullYear());
  const [gastos, setGastos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [aEliminar, setAEliminar] = useState(null);
  const [confirmarActivarTodos, setConfirmarActivarTodos] = useState(false);
  const [aLiquidar, setALiquidar] = useState(null);
  const [confirmarLiquidarTodo, setConfirmarLiquidarTodo] = useState(false);
  const esTotal = cuentaId === TOTAL_CUENTA_ID;

  const esTarjetaDe = (gasto) => cuentas.find((c) => c.cuentaId === gasto.cuentaId)?.tipo === "TarjetaCredito";
  const cuentaActualEsTarjeta = !esTotal && cuentas.find((c) => c.cuentaId === cuentaId)?.tipo === "TarjetaCredito";

  const cargar = async () => {
    if (!cuentaId) return;
    setCargando(true);
    try {
      if (esTotal) {
        const listas = await Promise.all(cuentas.map((c) => gastosFijosApi.listarGastosFijos(c.cuentaId, mes, anio)));
        setGastos(listas.flat());
      } else {
        setGastos(await gastosFijosApi.listarGastosFijos(cuentaId, mes, anio));
      }
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, [cuentaId]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePagado = async (gasto) => {
    await gastosFijosApi.marcarPagado(gasto.gastoFijoId, gasto.estado !== "Pagado");
    cargar();
    onSaldoCambiado?.();
  };

  const cambiarEstado = async (gasto, nuevoEstado) => {
    await gastosFijosApi.cambiarEstado(gasto.gastoFijoId, nuevoEstado);
    cargar();
  };

  const confirmarEliminar = async () => {
    await gastosFijosApi.eliminarGastoFijo(aEliminar.gastoFijoId);
    setAEliminar(null);
    cargar();
  };

  const activarTodos = async () => {
    await gastosFijosApi.activarTodosEnEspera(cuentaId, mes, anio);
    setConfirmarActivarTodos(false);
    cargar();
  };

  const confirmarLiquidar = async () => {
    await gastosFijosApi.liquidar(aLiquidar.gastoFijoId);
    setALiquidar(null);
    cargar();
  };

  const liquidarTodo = async () => {
    await gastosFijosApi.liquidarTodo(cuentaId, mes, anio);
    setConfirmarLiquidarTodo(false);
    cargar();
  };

  const totalPendiente = gastos.filter((g) => g.estado === "Pendiente").reduce((acc, g) => acc + g.monto, 0);
  const hayEnEspera = gastos.some((g) => g.estado === "En espera");
  const hayPagadosSinLiquidarEnTarjeta = cuentaActualEsTarjeta && gastos.some((g) => g.estado === "Pagado" && !g.liquidado);

  return (
    <div>
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
        <div className="text-muted small">
          Pendiente este mes: <strong className="text-fs-alerta">${formatoNumero(totalPendiente)}</strong>
        </div>
        <div className="d-flex gap-2">
          {!esTotal && hayEnEspera && !esAdministrador && (
            <Button size="sm" variant="outline-success" onClick={() => setConfirmarActivarTodos(true)}>
              <i className="bi bi-check2-all me-1"></i>Activar todos
            </Button>
          )}
          {hayPagadosSinLiquidarEnTarjeta && !esAdministrador && (
            <Button size="sm" variant="outline-secondary" onClick={() => setConfirmarLiquidarTodo(true)}>
              <i className="bi bi-lock me-1"></i>Liquidar todo
            </Button>
          )}
          {!esTotal && (
            <Button size="sm" variant="outline-primary" onClick={() => reportesApi.descargarGastosFijosMesPdf(cuentaId, mes, anio)}>
              <i className="bi bi-file-earmark-pdf me-1"></i>Imprimir
            </Button>
          )}
          {!esTotal && (
            <Button size="sm" variant="primary" onClick={() => { setEditando(null); setShowModal(true); }} disabled={esAdministrador} className={esAdministrador ? "d-none" : ""}>
              <i className="bi bi-plus-lg me-1"></i>Nuevo gasto fijo
            </Button>
          )}
        </div>
      </div>

      {cargando ? <Spinner animation="border" /> : (
        <div className="fs-card p-0 overflow-hidden">
          <Table responsive hover className="table-fs table-fs-responsive mb-0 align-middle">
            <thead>
              <tr>
                <th style={{ width: 50 }}>Pagado</th>
                <th>Motivo</th>
                <th className="text-end">Monto</th>
                <th>Fecha de cobro</th>
                <th>Estado</th>
                <th className="text-end">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {gastos.map((g) => {
                const porVencer = g.estado === "Pendiente" && diasHastaVencer(g) <= 1;
                return (
                <tr
                  key={g.gastoFijoId}
                  className={porVencer ? "bg-fs-gasto" : ""}
                  title={porVencer ? "Fecha de cobro por vencer" : undefined}
                >
                  <td data-label="Pagado">
                    <Form.Check
                      type="checkbox"
                      className="fs-check-pagado"
                      checked={g.estado === "Pagado"}
                      disabled={esAdministrador || g.estado === "En espera" || g.liquidado}
                      title={
                        g.liquidado
                          ? "Liquidado: ya no se puede destildar"
                          : g.estado === "En espera"
                          ? "Actívalo primero para poder marcarlo pagado"
                          : undefined
                      }
                      onChange={() => !esAdministrador && togglePagado(g)}
                    />
                    {g.liquidado && <i className="bi bi-lock-fill text-muted ms-1" title="Liquidado"></i>}
                  </td>
                  <td data-label="Motivo">{g.motivo}</td>
                  <td data-label="Monto" className="text-end fw-semibold">${formatoNumero(g.monto)}</td>
                  <td data-label="Fecha de cobro" className={porVencer ? "fw-semibold text-fs-gasto" : ""}>
                    {fechaCobroDe(g).toLocaleDateString("es-ES")}
                  </td>
                  <td data-label="Estado">
                    <Badge
                      bg={g.estado === "Pagado" ? "success" : g.estado === "En espera" ? "secondary" : "warning"}
                      text={g.estado === "Pendiente" ? "dark" : undefined}
                    >
                      {g.estado}
                    </Badge>
                  </td>
                  <td data-label="Acciones" className="text-end">
                    {!esAdministrador && (
                      <>
                        {g.estado === "Pendiente" && (
                          <Button size="sm" variant="outline-secondary" className="me-1" title="Poner en espera" onClick={() => cambiarEstado(g, "En espera")}>
                            <i className="bi bi-pause-circle"></i>
                          </Button>
                        )}
                        {g.estado === "En espera" && (
                          <Button size="sm" variant="outline-warning" className="me-1" title="Activar (pasar a Pendiente)" onClick={() => cambiarEstado(g, "Pendiente")}>
                            <i className="bi bi-play-circle"></i>
                          </Button>
                        )}
                        {g.estado === "Pagado" && !g.liquidado && esTarjetaDe(g) && (
                          <Button size="sm" variant="outline-secondary" className="me-1" title="Liquidar (bloquear para que no se destilde)" onClick={() => setALiquidar(g)}>
                            <i className="bi bi-lock"></i>
                          </Button>
                        )}
                        <Button size="sm" variant="outline-primary" className="me-1" onClick={() => { setEditando(g); setShowModal(true); }}>
                          <i className="bi bi-pencil"></i>
                        </Button>
                        <Button size="sm" variant="outline-danger" onClick={() => setAEliminar(g)}>
                          <i className="bi bi-trash"></i>
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
                );
              })}
              {!gastos.length && (
                <tr><td colSpan={6} className="text-center text-muted py-4">No hay gastos fijos este mes.</td></tr>
              )}
            </tbody>
          </Table>
        </div>
      )}

      <ModalEditarGastoFijo
        show={showModal}
        onHide={() => setShowModal(false)}
        gasto={editando}
        cuentaId={cuentaId}
        mes={mes}
        anio={anio}
        onGuardado={() => { setShowModal(false); cargar(); }}
      />
      <ModalConfirmar
        show={!!aEliminar}
        titulo="Eliminar gasto fijo"
        mensaje={`¿Eliminar el gasto fijo "${aEliminar?.motivo}"?`}
        onConfirmar={confirmarEliminar}
        onCancelar={() => setAEliminar(null)}
      />
      <ModalConfirmar
        show={confirmarActivarTodos}
        titulo="Activar todos los gastos en espera"
        mensaje="¿Pasar a Pendiente todos los gastos fijos que están En espera este mes? Empezarán a contar en el saldo disponible y podrás marcarlos como pagados."
        textoConfirmar="Activar todos"
        onConfirmar={activarTodos}
        onCancelar={() => setConfirmarActivarTodos(false)}
      />
      <ModalConfirmar
        show={!!aLiquidar}
        titulo="Liquidar gasto fijo"
        mensaje={`¿Liquidar "${aLiquidar?.motivo}"? Ya no vas a poder destildarlo como Pagado. No se puede deshacer, pero el mes que viene vuelve a nacer Pendiente como siempre.`}
        textoConfirmar="Liquidar"
        onConfirmar={confirmarLiquidar}
        onCancelar={() => setALiquidar(null)}
      />
      <ModalConfirmar
        show={confirmarLiquidarTodo}
        titulo="Liquidar todos los gastos pagados"
        mensaje="¿Liquidar todos los gastos fijos ya Pagado de esta tarjeta este mes? Ya no vas a poder destildarlos. No se puede deshacer, pero el mes que viene vuelven a nacer Pendiente como siempre."
        textoConfirmar="Liquidar todo"
        onConfirmar={liquidarTodo}
        onCancelar={() => setConfirmarLiquidarTodo(false)}
      />
    </div>
  );
}
